import React from 'react';
import GroupRow from 'features/activity/components/GroupRow';
import TrodoLoader from 'shared/components/ui/TrodoLoader';
import { DEFAULT_GROUP_COLUMNS, FIXED_GROUP_COLUMNS, getGroupColumnDefinition } from 'features/activity/config/groupColumns';
import {
  TABLE_STYLES,
  tableHeaderRowClass,
  tableHeaderCellClass,
} from 'features/activity/styles/dataTableStyles';
import TableFooter from 'shared/components/tables/TableFooter';

const GroupsTable = ({
  groups,
  loading,
  loadingMore = false,
  error,
  totalGroups,
  hasMore = false,
  onLoadMore,
  hasSearchFilter,
  searchTerm,
  columnConfig,
  onRowClick,
  groupKey,
  /** Merged keys from API + prefs (optional); improves headers for columns not on current page */
  knownCustomPropKeys = []
}) => {
  const config = columnConfig || DEFAULT_GROUP_COLUMNS;
  const customPropKeys = React.useMemo(() => {
    const keys = new Set(knownCustomPropKeys);
    (groups || []).forEach((g) => {
      Object.keys(g.customProperties || {}).forEach((k) => keys.add(k));
    });
    return [...keys];
  }, [groups, knownCustomPropKeys]);

  const fixedGroupKeys = Object.keys(FIXED_GROUP_COLUMNS);
  let visibleOrder = (config.order || []).filter((key) => (config.enabled || []).includes(key));
  fixedGroupKeys.forEach((f) => {
    if (!visibleOrder.includes(f)) visibleOrder.unshift(f);
  });
  const styles = TABLE_STYLES;
  const shellClass =
    'overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface';

  if (loading) {
    return (
      <div className={shellClass}>
        <div className="flex flex-col items-center justify-center px-6 py-12">
          <TrodoLoader size={100} label="Loading groups" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={shellClass}>
        <div className="px-6 py-8 text-center text-[12px]" style={{ color: 'var(--feedback-error)', fontFamily: 'Inter, sans-serif' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  if (!groups || groups.length === 0) {
    return (
      <div className={shellClass}>
        <div className="px-6 py-8 text-center text-[12px] text-[color:var(--text-secondary)]" style={{ fontFamily: 'Inter, sans-serif' }}>
          {hasSearchFilter && searchTerm ? <>No groups found matching your search.</> : <>No groups found.</>}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-2 px-1 text-left">
        <div className="text-[12px] text-[color:var(--text-muted)]" style={{ fontFamily: 'Inter, sans-serif' }}>
          Showing <span className="font-semibold">{groups.length}</span> most recent results of{' '}
          {Number(totalGroups || 0).toLocaleString()} matches
        </div>
      </div>

      <div
        className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left" style={{ fontFamily: 'Inter, sans-serif' }}>
            <thead>
              <tr className={tableHeaderRowClass}>
                {visibleOrder.map((key) => {
                  const def = getGroupColumnDefinition(key, customPropKeys);
                  const label = def ? def.label : key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  return (
                    <th key={key} className={tableHeaderCellClass}>
                      {label}
                    </th>
                  );
                })}
                <th className={tableHeaderCellClass} />
              </tr>
            </thead>
            <tbody>
              {groups.map((group, index) => (
                <GroupRow
                  key={`${group.groupKey}-${group.groupId}-${index}`}
                  group={group}
                  onRowClick={onRowClick}
                  columns={visibleOrder}
                  styles={styles}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <TableFooter
        shown={groups.length}
        total={totalGroups}
        hasMore={hasMore}
        loading={loadingMore}
        onLoadMore={onLoadMore}
      />
    </>
  );
};

export default GroupsTable;
